import {
  Box,
  Button,
  ButtonGroup,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import DeleteIcon from "@material-ui/icons/Delete";
import EmailIcon from "@material-ui/icons/Email";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Form, Formik } from "formik";
import MaterialTable from "material-table";
import { useConfirm } from "material-ui-confirm";
import LabelsContext from "../../../lang/LabelsContext";
import ArticleDto from "../../../model/manuscript/ArticleDto";
import EnumArticleReviewStatus from "../../../utils/enum/EnumArticleReviewStatus";
import { dateView, muiControls } from "../../../utils/Utils";
import DialogAction from "../../system/dialog-action/DialogAction";
import { tableIcons } from "../../system/material-table-utils/MaterialTableUtils";
import RichText from "../../system/rich-text/RichText";
import ArticleReviwerService, { ArticleReviwerServiceType } from "./ArticleReviwerService";
import { useStyles } from "./ArticleReviwerStyle";

const assignSameReviwersDesc =
  "Reviewers from the previous revision are assigned! Reviewers are selected, but not mailed automatically. You can mail reviewers, remove them from the list or add new reviewers to this revision.";

const cellStyle = {
  padding: 0,
};

const headerStyle = {
  padding: 0,
};

interface ArticleReviwerType {
  article?: ArticleDto;
}

export default function ArticleReviwer({ article }: ArticleReviwerType) {
  const {
    sendMailToReviewer,
    handleCloseDialog,
    openDialog,
    mailSuggestion,
    numberArray,
    setReviewersCount,
    active,
    AddReviewer,
    DeleteReviewer,
    addedReviewers,
    sendMailToAllReviewers,
    handleOpenDialog,
    reviewers,
    mailRef,
    assignSameReviwers,
  }: ArticleReviwerServiceType = ArticleReviwerService({ article });
  const classes = useStyles();
  const { Labels } = LabelsContext();
  const confirm = useConfirm();

  const confirmDialog = (description: string, execute: any) => {
    return confirm({
      description: description,
      confirmationText: Labels.YES,
      cancellationText: Labels.NO,
      confirmationButtonProps: {
        variant: "contained",
        color: "primary",
      },
    })
      .then(execute)
      .catch(() => {});
  };

  return (
    <div>
      <Grid item xs={12} sm={12} className={classes.cardContainer}>
        <Card className={classes.card}>
          <Typography variant="h6" className={classes.paddingBottom}>
            {Labels.REQUIRED_REVIEWERS}
          </Typography>
          <ButtonGroup size="small" color="primary" aria-label="outlined primary button group">
            {numberArray?.map((num: number) => (
              <Button
                key={num}
                onClick={() => setReviewersCount(num)}
                variant={active === num ? "contained" : undefined}
              >
                {num}
              </Button>
            ))}
          </ButtonGroup>
        </Card>
      </Grid>
      <Grid item xs={12} sm={12}>
        <Grid item xs={12} sm={12}>
          <MaterialTable
            icons={tableIcons}
            columns={[
              { field: "id", title: "ID", hidden: true },
              { field: "reviewer.firstName", title: Labels.FIRSTNAME },
              { field: "reviewer.lastName", title: Labels.LASTNAME, defaultSort: "asc" },
              {
                field: "reviewer.areasOfExpertise",
                sorting: true,
                title: Labels.AREAS_OF_EXPERTISE,
                render: (rowData: any) => (
                  <Tooltip title={<h3>{rowData.reviewer.areasOfExpertise}</h3>}>
                    <span>
                      {rowData.reviewer.areasOfExpertise.length > 70
                        ? rowData.reviewer.areasOfExpertise.substring(0, 70) + "..."
                        : rowData.reviewer.areasOfExpertise}
                    </span>
                  </Tooltip>
                ),
              },
              {
                field: "status",
                title: Labels.STATUS,
                render: (rowData: any) => (
                  <div className={classes.statusInline}>
                    <div> {rowData.status}</div>
                    <Tooltip
                      title={
                        <div>
                          {rowData.selectedDate && (
                            <>
                              <div className={classes.tooltip}>
                                {Labels.SELECTED_ON}: {dateView(rowData.selectedDate)}
                              </div>
                              <br />
                            </>
                          )}
                          {rowData.mailedDate && (
                            <>
                              <div className={classes.tooltip}>
                                {Labels.MAILED_ON}: {dateView(rowData.mailedDate)}
                              </div>
                              <br />
                            </>
                          )}
                          {rowData.decidedDate && (
                            <>
                              <div className={classes.tooltip}>
                                {Labels.DECIDED_ON}: {dateView(rowData.decidedDate)}
                              </div>
                              <br />
                            </>
                          )}
                          {rowData.completedDate && (
                            <>
                              <div className={classes.tooltip}>
                                {Labels.DECIDED_ON}: {dateView(rowData.completedDate)}
                              </div>
                              <br />
                            </>
                          )}
                          {rowData.review?.recommendation && (
                            <>
                              <div className={classes.tooltip}>
                                {"Recommendation: "}
                                {rowData.review?.recommendation}
                              </div>
                            </>
                          )}
                        </div>
                      }
                    >
                      <HelpOutlineIcon fontSize="small" className={classes.helpIcon} />
                    </Tooltip>
                  </div>
                ),
              },
              {
                field: "avgRating",
                title: Labels.RATING,
                render: (rowData: any) => (
                  <div>{rowData.avgRating ? parseFloat(rowData.avgRating).toFixed(2) : ""}</div>
                ),
              },
            ]}
            title={
              <div className={classes.mailToAll}>
                <Box display="flex" alignItems="center">
                  <Typography variant="h6">{Labels.ASSIGNED_REVIEWERS}</Typography>
                  {addedReviewers.length > 0 && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      className={classes.mailAllButton}
                      onClick={() => confirmDialog(Labels.SEND_MAIL_TO_ALL, sendMailToAllReviewers)}
                    >
                      {Labels.MAIL_ALL}
                    </Button>
                  )}
                </Box>
                {article?.revision !== 0 && addedReviewers.length === 0 && (
                  <Box display="flex" alignItems="center">
                    <Typography variant="h6">Do you want to assign reviwers from the previous revision?</Typography>
                    <DialogAction
                      buttonName={Labels.YES}
                      buttonClass={classes.mailAllButton}
                      title={"Info"}
                      description={assignSameReviwersDesc}
                      action={assignSameReviwers}
                    />
                  </Box>
                )}
              </div>
            }
            data={addedReviewers}
            options={{
              actionsColumnIndex: -1,
              paging: false,
            }}
            actions={[
              (rowData) => ({
                icon: () =>
                  rowData.status !== EnumArticleReviewStatus.MAILED ? (
                    <EmailIcon color="action" onClick={() => handleOpenDialog(rowData.id)} />
                  ) : (
                    <CheckCircleIcon color="action" />
                  ),
                tooltip: rowData.status !== EnumArticleReviewStatus.MAILED ? Labels.SEND_MAIL : Labels.MAIL_SENT,
                onClick: (event, rowData: any) => () => handleOpenDialog(rowData.id),
              }),
              {
                icon: () => <DeleteIcon color="action" />,
                onClick: (event, rowData: any) => DeleteReviewer(rowData.id),
              },
            ]}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} className={classes.marginTop3}>
        <Grid item xs={12} sm={12}>
          <MaterialTable
            icons={tableIcons}
            columns={[
              {
                field: "id",
                title: "ID",
                hidden: true,
                cellStyle: cellStyle,
                headerStyle: headerStyle,
                align: "center",
              },
              {
                field: "firstName",
                title: Labels.FIRSTNAME,
                cellStyle: cellStyle,
                headerStyle: headerStyle,
                align: "center",
              },
              {
                field: "lastName",
                title: Labels.LASTNAME,
                defaultSort: "asc",
                align: "center",
                cellStyle: cellStyle,
                headerStyle: headerStyle,
              },
              {
                field: "expertise",
                sorting: true,
                title: Labels.AREAS_OF_EXPERTISE,
                align: "center",
                cellStyle: cellStyle,
                headerStyle: headerStyle,
                render: (rowData) => (
                  <Tooltip title={<h3>{rowData.expertise}</h3>}>
                    <span>
                      {rowData.expertise.length > 60 ? rowData.expertise.substring(0, 60) + "..." : rowData.expertise}
                    </span>
                  </Tooltip>
                ),
              },
              {
                field: "currentAsignments",
                title: Labels.CURRENT_ASSIGNMENTS,
                cellStyle: cellStyle,
                align: "center",
              },
              {
                field: "completedLastYear",
                title: Labels.COMPLETED_LAST_YEAR,
                cellStyle: cellStyle,
                align: "center",
              },
              {
                field: "assignmentsLastYear",
                title: Labels.ASSIGNMENTS_LAST_YEAR,
                cellStyle: cellStyle,
                align: "center",
              },
              {
                field: "responseTime",
                sorting: true,
                align: "center",
                title: Labels.RESPONSE_TIME_DAYS,
                cellStyle: cellStyle,
                headerStyle: headerStyle,
                render: (rowData) => <div>{parseFloat(rowData.responseTime).toFixed(2)}</div>,
              },

              {
                field: "rejectPct",
                title: Labels.REJECTED,
                cellStyle: cellStyle,
                align: "center",
                headerStyle: headerStyle,
                render: (rowData) => <div>{parseFloat(rowData.rejectPct).toFixed(2)}</div>,
              },
              {
                field: "acceptedArticles",
                title: Labels.PUBLISHED_LAST_36_MONTHS,
              },
              {
                field: "avgRating",
                title: Labels.RATING,
                align: "center",
                cellStyle: cellStyle,
                headerStyle: headerStyle,
                render: (rowData: any) => (
                  <div>{rowData.avgRating ? parseFloat(rowData.avgRating).toFixed(2) : ""}</div>
                ),
              },
            ]}
            title={Labels.ASSIGN_NEW_REVIEWERS}
            data={reviewers}
            options={{
              paging: false,
              actionsColumnIndex: -1,
              maxBodyHeight: "1000px",
            }}
            actions={[
              {
                icon: () => <AddCircleIcon color="action" />,
                onClick: (event, rowData: any) => AddReviewer(rowData.id),
              },
            ]}
          />
        </Grid>
      </Grid>
      <Dialog
        maxWidth={"md"}
        fullWidth={true}
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">{Labels.NEW_EMAIL}</DialogTitle>
        {mailSuggestion && (
          <Formik
            initialValues={{
              mailto: mailSuggestion.to ? mailSuggestion.to : [],
              cc: mailSuggestion.cc ? mailSuggestion.cc : [],
              subject: mailSuggestion.subject ? mailSuggestion.subject : "",
              message: mailSuggestion.message ? mailSuggestion.message : "",
            }}
            onSubmit={(values) => {
              sendMailToReviewer(values);
              handleCloseDialog();
            }}
          >
            {({ handleBlur, values, touched, errors, handleChange, handleSubmit, setFieldValue }) => (
              <Form>
                <DialogContent>
                  <Grid container direction="column">
                    <Grid item>
                      <Autocomplete
                        className={classes.marginBottom2}
                        multiple
                        id="tags-mailto"
                        value={values.mailto}
                        onChange={(event, value) => setFieldValue("mailto", value)}
                        options={[{ title: "exampleTest@gmail.com" }].map((option) => option.title)}
                        freeSolo
                        renderTags={(value: string[], getTagProps) =>
                          value.map((option: string, index: number) => (
                            <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                          ))
                        }
                        renderInput={(params) => <TextField {...params} label={Labels.MAILTO} variant="outlined" />}
                      />
                    </Grid>
                    {/* <Grid item>
                          <Autocomplete
                            multiple
                            id="tags-cc"
                            value={values.cc}
                            onChange={(event, value) => setFieldValue("cc", value)}
                            options={[{ title: "exampleTest@gmail.com" }].map((option) => option.title)}
                            freeSolo
                            renderTags={(value: string[], getTagProps) =>
                              value.map((option: string, index: number) => (
                                <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                              ))
                            }
                            renderInput={(params) => <TextField {...params} label={Labels.CC} variant="outlined" />}
                          />
                        </Grid> */}
                    <Grid item>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        id="subject"
                        name="subject"
                        label={Labels.SUBJECT}
                        value={values.subject}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.subject && Boolean(errors.subject)}
                        helperText={touched.subject && errors.subject}
                        fullWidth
                      />
                    </Grid>
                    <Grid item>
                      <RichText
                        muiEditorRef={mailRef}
                        placeholder={Labels.PLACEHOLDER_WRITE_COMMENT}
                        controls={muiControls}
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCloseDialog} color="primary">
                    {Labels.CANCEL}
                  </Button>
                  <Button variant="contained" color="primary" type="submit">
                    {Labels.SEND}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        )}
      </Dialog>
    </div>
  );
}
